import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddSsoConnectionModalComponent } from 'src/app/modals/add-sso-connection-modal/add-sso-connection-modal.component';
import { ToastService } from 'src/app/services/toast.service';
import { ssoConnectionsService } from 'src/app/shared/vive-api/sso-connections.service';
import { SsoConnectionDto } from 'src/app/shared/vive-api/vive-api-models';

import {
  SSO_PROTOCOL_LABELS,
  SSO_SAVE_FAILED,
  SSO_STATUS_CHIPS,
  confirmSsoDisable,
  hasDomainAndMapping,
  loadClientNames,
  ssoApiErrorMessage,
  ssoConnectionForLabel,
  ssoLoadErrorMessage
} from './sso-connections';

@Component({
  selector: 'app-admin-admin-sso-connections',
  templateUrl: './admin-admin-sso-connections.component.html',
  styleUrl: './admin-admin-sso-connections.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminAdminSsoConnectionsComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  public readonly protocolLabels = SSO_PROTOCOL_LABELS;
  public readonly statusChips = SSO_STATUS_CHIPS;
  public readonly hasDomainAndMapping = hasDomainAndMapping;

  public connections: SsoConnectionDto[] = [];
  public loading = true;
  public loadError: string | null = null;
  public actionError: string | null = null;
  public busyConnectionId: number | null = null;
  private clientNames: ReadonlyMap<number, string> = new Map();

  public async ngOnInit(): Promise<void> {
    try {
      const connections = await ssoConnectionsService.findAll();
      this.clientNames = await loadClientNames(
        connections.flatMap(({ CompanyId }) =>
          CompanyId === null ? [] : [CompanyId]
        )
      );
      this.connections = connections;
    } catch (error) {
      this.loadError = ssoLoadErrorMessage(error);
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  public forLabel(connection: SsoConnectionDto): string {
    return ssoConnectionForLabel(connection, this.clientNames);
  }

  public domainsLabel(connection: SsoConnectionDto): string {
    return (connection.Domains ?? []).map(({ domain }) => domain).join(', ');
  }

  public async addConnection(): Promise<void> {
    const { result } = this.modalService.open(AddSsoConnectionModalComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md'
    });

    let created: SsoConnectionDto | undefined;
    try {
      created = await result;
    } catch {
      // Cancel, Close and Escape dismiss the dialog.
      return;
    }

    if (created) {
      await this.router.navigate([
        '/admin',
        'admin',
        'sso-connections',
        created.id
      ]);
    }
  }

  public async activate(connection: SsoConnectionDto): Promise<void> {
    await this.changeStatus(connection, async () => {
      const updated = await ssoConnectionsService.activate({
        pathParams: { connectionId: connection.id }
      });
      this.toastService.addSuccessToast(
        'Activated',
        `${updated.name} is active.`
      );

      return updated;
    });
  }

  public async disable(connection: SsoConnectionDto): Promise<void> {
    if (!(await confirmSsoDisable(this.modalService, connection.name))) {
      return;
    }

    await this.changeStatus(connection, async () => {
      const updated = await ssoConnectionsService.disable({
        pathParams: { connectionId: connection.id }
      });
      this.toastService.addSuccessToast(
        'Disabled',
        `${updated.name} is disabled.`
      );

      return updated;
    });
  }

  private async changeStatus(
    connection: SsoConnectionDto,
    change: () => Promise<SsoConnectionDto>
  ): Promise<void> {
    this.busyConnectionId = connection.id;
    this.actionError = null;
    this.cdr.markForCheck();

    try {
      const updated = await change();
      this.connections = this.connections.map((row) =>
        row.id === updated.id ? updated : row
      );
    } catch (error) {
      this.actionError = ssoApiErrorMessage(error, SSO_SAVE_FAILED);
    } finally {
      this.busyConnectionId = null;
      this.cdr.markForCheck();
    }
  }
}
